import { useState } from 'react';
import { Card, Select, Table, Tag, Alert, Row, Col, Statistic, Space, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { kullanimApi } from '../api/monitoring';
import type { KullanimKullanici, KullanimRapor } from '../api/monitoring';

const { Text } = Typography;

const PENCERELER = [7, 30, 90, 365];

/** Tarihi gün/ay/yıl + saat olarak basar; yoksa '—' (BOŞ DEĞİL: ayrım görünür olmalı). */
function tarih(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleString('tr-TR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** Gün farkı — bugüne göre. `null` girdi `null` döner (0 DEĞİL). */
function gunOnce(v: string | null | undefined): number | null {
  if (!v) return null;
  return Math.floor((Date.now() - new Date(v).getTime()) / 86_400_000);
}

export function Kullanim() {
  const [gun, setGun] = useState(30);
  const { data, isLoading } = useQuery({
    queryKey: ['kullanim', gun],
    queryFn: () => kullanimApi.getir(gun),
  });

  const kullanicilar = data?.kullanicilar ?? [];
  const raporlar = data?.raporlar ?? [];

  const toplamSorgu = kullanicilar.reduce((a, k) => a + k.sorgu, 0);
  const aktifKullanici = kullanicilar.filter((k) => k.sorgu > 0).length;
  const aktifFirma = new Set(
    kullanicilar.filter((k) => k.sorgu > 0).map((k) => k.firmaId),
  ).size;

  const kullaniciKolonlari = [
    { title: 'Firma', dataIndex: 'firma', key: 'firma',
      render: (v: string | null) => v ?? <Text type="secondary">(sistem)</Text> },
    { title: 'Kullanıcı', dataIndex: 'kullanici', key: 'kullanici',
      render: (v: string, r: KullanimKullanici) => (
        <Space direction="vertical" size={0}>
          <span>{v}</span>
          <Text type="secondary" style={{ fontSize: 12 }}>{r.eposta}</Text>
        </Space>
      ) },
    /**
     * 🔴 "Son giriş" ve "Son işlem" AYRI iki kolon ve bu ölçülmüş bir karar:
     * refresh token oturumu haftalarca ayakta tutuyor. 28 kullanıcının 5'i son
     * girişinden 7 günden fazla SONRA hâlâ aktifti, en uzun fark 54 gün. Tek kolon
     * olsaydı bugün sistemi kullanan biri "Haziran'dan beri girmemiş" görünürdü.
     */
    { title: 'Son giriş', dataIndex: 'sonGiris', key: 'sonGiris',
      sorter: (a: KullanimKullanici, b: KullanimKullanici) =>
        (a.sonGiris ?? '').localeCompare(b.sonGiris ?? ''),
      render: (v: string | null) => tarih(v) },
    { title: 'Son işlem', dataIndex: 'sonIslem', key: 'sonIslem',
      sorter: (a: KullanimKullanici, b: KullanimKullanici) =>
        (a.sonIslem ?? '').localeCompare(b.sonIslem ?? ''),
      render: (v: string | null, r: KullanimKullanici) => {
        const g = gunOnce(v);
        const gg = gunOnce(r.sonGiris);
        // Girişten belirgin şekilde SONRA hâlâ aktifse bunu görünür kıl — ekranın
        // taşıdığı asıl bilgi bu.
        const sarkiyor = g != null && gg != null && gg - g > 7;
        return (
          <Space size={4}>
            <span>{tarih(v)}</span>
            {sarkiyor && <Tag color="blue">giriş sonrası {gg! - g!} gün</Tag>}
          </Space>
        );
      } },
    { title: 'Sorgu', dataIndex: 'sorgu', key: 'sorgu', align: 'right' as const,
      defaultSortOrder: 'descend' as const,
      sorter: (a: KullanimKullanici, b: KullanimKullanici) => a.sorgu - b.sorgu,
      render: (v: number) => v.toLocaleString('tr-TR') },
    { title: 'Aktif gün', dataIndex: 'aktifGun', key: 'aktifGun', align: 'right' as const,
      sorter: (a: KullanimKullanici, b: KullanimKullanici) => a.aktifGun - b.aktifGun },
    { title: 'Giriş', dataIndex: 'giris', key: 'giris', align: 'right' as const,
      sorter: (a: KullanimKullanici, b: KullanimKullanici) => a.giris - b.giris },
    { title: 'Durum', dataIndex: 'aktif', key: 'aktif',
      render: (v: boolean) => v ? <Tag color="green">aktif</Tag> : <Tag>kapalı</Tag> },
  ];

  const raporKolonlari = [
    { title: 'Rapor', dataIndex: 'rapor', key: 'rapor',
      render: (v: string | null, r: KullanimRapor) => r.silinmis
        ? <Space size={4}><Text type="secondary">#{r.kaynakId}</Text><Tag color="orange">silinmiş kaynak</Tag></Space>
        : <span>{v}</span> },
    { title: 'ds', dataIndex: 'kaynakId', key: 'kaynakId', align: 'right' as const },
    { title: 'Çalışma', dataIndex: 'calisma', key: 'calisma', align: 'right' as const,
      render: (v: number) => v.toLocaleString('tr-TR') },
    { title: 'Kullanıcı', dataIndex: 'kullanici', key: 'kullanici', align: 'right' as const },
    { title: 'Son çalışma', dataIndex: 'sonCalisma', key: 'sonCalisma',
      render: (v: string) => tarih(v) },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Space>
          <span>Dönem:</span>
          <Select
            style={{ width: 160 }}
            value={gun}
            onChange={setGun}
            options={PENCERELER.map((g) => ({ value: g, label: `Son ${g} gün` }))}
          />
          {data?.denetimBasladi && (
            <Text type="secondary">Denetim kaydı {tarih(data.denetimBasladi)} tarihinde başladı.</Text>
          )}
        </Space>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}><Card><Statistic title="Sorgu çalıştıran kullanıcı" value={aktifKullanici} suffix={`/ ${kullanicilar.length}`} /></Card></Col>
        <Col xs={24} sm={8}><Card><Statistic title="Sorgu çalıştıran firma" value={aktifFirma} /></Card></Col>
        <Col xs={24} sm={8}><Card><Statistic title="Toplam sorgu" value={toplamSorgu} /></Card></Col>
      </Row>

      <Card title="Firma ve kullanıcı bazlı kullanım" style={{ marginBottom: 16 }}>
        <Table
          rowKey="kullaniciId"
          loading={isLoading}
          dataSource={kullanicilar}
          columns={kullaniciKolonlari}
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Card title="En çok çalıştırılan raporlar">
        {/*
          🔴 BOŞ LİSTE "KULLANILMIYOR" DEĞİL "ÖLÇÜLMEDİ" DEMEK. Rapor kırılımı
          2026-08-19'da eklendi ve GEÇMİŞ DOLMAZ; ondan önceki 72 bin sorgu hangi
          raporu çalıştırdığını söyleyemez. Sayacın sıfırını sessizce basmak, bu
          oturumda üç yerde düzeltilen kusurun (admin panosu "%100 başarı", Grafana
          5xx kutusu, hub agent `status`) aynısı olurdu.
        */}
        {!data?.raporKaydiBasladi ? (
          <Alert
            type="info"
            showIcon
            message="Bu kırılım henüz ölçülmedi"
            description={
              'Hangi raporun çalıştırıldığı bilgisi yeni eklendi ve kayıt dağıtımdan '
              + 'itibaren birikiyor. Geçmiş doldurulamıyor: daha önceki sorgular hangi '
              + 'raporu çalıştırdığını taşımıyor. İlk kayıtlar geldiğinde bu tablo dolacak.'
            }
          />
        ) : (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`Kayıt ${tarih(data?.raporKaydiBasladi)} tarihinde başladı — öncesi ölçülmedi.`}
            />
            <Table
              rowKey="kaynakId"
              loading={isLoading}
              dataSource={raporlar}
              columns={raporKolonlari}
              size="small"
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          </>
        )}
      </Card>
    </div>
  );
}
